<template>
    <v-dialog v-model="visible" fullscreen scrollable transition="dialog-bottom-transition">
        <v-card class="operations-card">
            <!-- Header -->
            <div class="card-header">
                <div class="left-section">
                    <div class="header-badge">{{ $t('operations.header.badge') }}</div>
                    <h2>{{ $t('operations.header.title') }}</h2>
                    <p class="header-description">
                        {{ $t('operations.header.description') }}
                    </p>
                </div>
                <div class="right-section">
                    <button @click="closeDialog" class="dialog-close-btn">
                        <v-icon>mdi-close</v-icon>
                    </button>
                </div>
            </div>

            <!-- Main Content -->
            <div class="operations-container">
                <!-- Ticket Workflow -->
                <div class="workflow-section">
                    <div class="section-header">
                        <h3>{{ $t('operations.workflow.title') }}</h3>
                        <p>{{ $t('operations.workflow.subtitle') }}</p>
                    </div>

                    <div class="workflow-diagram">
                        <div class="workflow-step">
                            <div class="step-number">1</div>
                            <div class="step-content">
                                <div class="step-icon">
                                    <v-icon size="28" color="white">mdi-file-document-edit</v-icon>
                                </div>
                                <h4>{{ $t('operations.workflow.steps.submit.title') }}</h4>
                                <p>{{ $t('operations.workflow.steps.submit.description') }}</p>
                            </div>
                        </div>

                        <div class="workflow-arrow">
                            <v-icon size="24">mdi-arrow-right</v-icon>
                        </div>

                        <div class="workflow-step">
                            <div class="step-number">2</div>
                            <div class="step-content">
                                <div class="step-icon">
                                    <v-icon size="28" color="white">mdi-robot</v-icon>
                                </div>
                                <h4>{{ $t('operations.workflow.steps.categorize.title') }}</h4>
                                <p>{{ $t('operations.workflow.steps.categorize.description') }}</p>
                            </div>
                        </div>

                        <div class="workflow-arrow">
                            <v-icon size="24">mdi-arrow-right</v-icon>
                        </div>

                        <div class="workflow-step">
                            <div class="step-number">3</div>
                            <div class="step-content">
                                <div class="step-icon">
                                    <v-icon size="28" color="white">mdi-account-tie</v-icon>
                                </div>
                                <h4>{{ $t('operations.workflow.steps.assign.title') }}</h4>
                                <p>{{ $t('operations.workflow.steps.assign.description') }}</p>
                            </div>
                        </div>

                        <div class="workflow-arrow">
                            <v-icon size="24">mdi-arrow-right</v-icon>
                        </div>

                        <div class="workflow-step">
                            <div class="step-number">4</div>
                            <div class="step-content">
                                <div class="step-icon">
                                    <v-icon size="28" color="white">mdi-tools</v-icon>
                                </div>
                                <h4>{{ $t('operations.workflow.steps.investigate.title') }}</h4>
                                <p>{{ $t('operations.workflow.steps.investigate.description') }}</p>
                            </div>
                        </div>

                        <div class="workflow-arrow">
                            <v-icon size="24">mdi-arrow-right</v-icon>
                        </div>

                        <div class="workflow-step">
                            <div class="step-number">5</div>
                            <div class="step-content">
                                <div class="step-icon">
                                    <v-icon size="28" color="white">mdi-check-bold</v-icon>
                                </div>
                                <h4>{{ $t('operations.workflow.steps.resolve.title') }}</h4>
                                <p>{{ $t('operations.workflow.steps.resolve.description') }}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Issue Categories -->
                <div class="categories-section">
                    <div class="section-header">
                        <h3>{{ $t('operations.categories.title') }}</h3>
                        <p>{{ $t('operations.categories.subtitle') }}</p>
                    </div>

                    <v-row class="categories-grid">
                        <!-- Login Issues -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="category-card login">
                                <div class="category-header">
                                    <div class="category-icon">
                                        <v-icon size="36" color="white">mdi-login-variant</v-icon>
                                    </div>
                                    <div class="priority-badge high">{{ $t('operations.categories.login.priority') }}</div>
                                </div>
                                <h4>{{ $t('operations.categories.login.title') }}</h4>
                                <p>{{ $t('operations.categories.login.description') }}</p>
                                <div class="issue-list">
                                    <div class="issue-item">
                                        <v-icon size="14">mdi-alert-circle</v-icon>
                                        <span>{{ $t('operations.categories.login.issue1') }}</span>
                                    </div>
                                    <div class="issue-item">
                                        <v-icon size="14">mdi-alert-circle</v-icon>
                                        <span>{{ $t('operations.categories.login.issue2') }}</span>
                                    </div>
                                    <div class="issue-item">
                                        <v-icon size="14">mdi-alert-circle</v-icon>
                                        <span>{{ $t('operations.categories.login.issue3') }}</span>
                                    </div>
                                    <div class="issue-item">
                                        <v-icon size="14">mdi-alert-circle</v-icon>
                                        <span>{{ $t('operations.categories.login.issue4') }}</span>
                                    </div>
                                </div>
                                <div class="category-stats">
                                    <span class="stat-badge">{{ $t('operations.categories.login.avgResolution') }}</span>
                                </div>
                            </div>
                        </v-col>

                        <!-- App Performance -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="category-card performance">
                                <div class="category-header">
                                    <div class="category-icon">
                                        <v-icon size="36" color="white">mdi-speedometer</v-icon>
                                    </div>
                                    <div class="priority-badge medium">{{ $t('operations.categories.performance.priority') }}</div>
                                </div>
                                <h4>{{ $t('operations.categories.performance.title') }}</h4>
                                <p>{{ $t('operations.categories.performance.description') }}</p>
                                <div class="issue-list">
                                    <div class="issue-item">
                                        <v-icon size="14">mdi-alert-circle</v-icon>
                                        <span>{{ $t('operations.categories.performance.issue1') }}</span>
                                    </div>
                                    <div class="issue-item">
                                        <v-icon size="14">mdi-alert-circle</v-icon>
                                        <span>{{ $t('operations.categories.performance.issue2') }}</span>
                                    </div>
                                    <div class="issue-item">
                                        <v-icon size="14">mdi-alert-circle</v-icon>
                                        <span>{{ $t('operations.categories.performance.issue3') }}</span>
                                    </div>
                                    <div class="issue-item">
                                        <v-icon size="14">mdi-alert-circle</v-icon>
                                        <span>{{ $t('operations.categories.performance.issue4') }}</span>
                                    </div>
                                </div>
                                <div class="category-stats">
                                    <span class="stat-badge">{{ $t('operations.categories.performance.avgResolution') }}</span>
                                </div>
                            </div>
                        </v-col>

                        <!-- Notification Problems -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="category-card notifications">
                                <div class="category-header">
                                    <div class="category-icon">
                                        <v-icon size="36" color="white">mdi-bell-alert</v-icon>
                                    </div>
                                    <div class="priority-badge medium">{{ $t('operations.categories.notifications.priority') }}</div>
                                </div>
                                <h4>{{ $t('operations.categories.notifications.title') }}</h4>
                                <p>{{ $t('operations.categories.notifications.description') }}</p>
                                <div class="issue-list">
                                    <div class="issue-item">
                                        <v-icon size="14">mdi-alert-circle</v-icon>
                                        <span>{{ $t('operations.categories.notifications.issue1') }}</span>
                                    </div>
                                    <div class="issue-item">
                                        <v-icon size="14">mdi-alert-circle</v-icon>
                                        <span>{{ $t('operations.categories.notifications.issue2') }}</span>
                                    </div>
                                    <div class="issue-item">
                                        <v-icon size="14">mdi-alert-circle</v-icon>
                                        <span>{{ $t('operations.categories.notifications.issue3') }}</span>
                                    </div>
                                    <div class="issue-item">
                                        <v-icon size="14">mdi-alert-circle</v-icon>
                                        <span>{{ $t('operations.categories.notifications.issue4') }}</span>
                                    </div>
                                </div>
                                <div class="category-stats">
                                    <span class="stat-badge">{{ $t('operations.categories.notifications.avgResolution') }}</span>
                                </div>
                            </div>
                        </v-col>

                        <!-- Data Sync Issues -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="category-card data-sync">
                                <div class="category-header">
                                    <div class="category-icon">
                                        <v-icon size="36" color="white">mdi-cloud-sync</v-icon>
                                    </div>
                                    <div class="priority-badge high">{{ $t('operations.categories.dataSync.priority') }}</div>
                                </div>
                                <h4>{{ $t('operations.categories.dataSync.title') }}</h4>
                                <p>{{ $t('operations.categories.dataSync.description') }}</p>
                                <div class="issue-list">
                                    <div class="issue-item">
                                        <v-icon size="14">mdi-alert-circle</v-icon>
                                        <span>{{ $t('operations.categories.dataSync.issue1') }}</span>
                                    </div>
                                    <div class="issue-item">
                                        <v-icon size="14">mdi-alert-circle</v-icon>
                                        <span>{{ $t('operations.categories.dataSync.issue2') }}</span>
                                    </div>
                                    <div class="issue-item">
                                        <v-icon size="14">mdi-alert-circle</v-icon>
                                        <span>{{ $t('operations.categories.dataSync.issue3') }}</span>
                                    </div>
                                    <div class="issue-item">
                                        <v-icon size="14">mdi-alert-circle</v-icon>
                                        <span>{{ $t('operations.categories.dataSync.issue4') }}</span>
                                    </div>
                                </div>
                                <div class="category-stats">
                                    <span class="stat-badge">{{ $t('operations.categories.dataSync.avgResolution') }}</span>
                                </div>
                            </div>
                        </v-col>

                        <!-- Device Compatibility -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="category-card compatibility">
                                <div class="category-header">
                                    <div class="category-icon">
                                        <v-icon size="36" color="white">mdi-devices</v-icon>
                                    </div>
                                    <div class="priority-badge low">{{ $t('operations.categories.compatibility.priority') }}</div>
                                </div>
                                <h4>{{ $t('operations.categories.compatibility.title') }}</h4>
                                <p>{{ $t('operations.categories.compatibility.description') }}</p>
                                <div class="issue-list">
                                    <div class="issue-item">
                                        <v-icon size="14">mdi-alert-circle</v-icon>
                                        <span>{{ $t('operations.categories.compatibility.issue1') }}</span>
                                    </div>
                                    <div class="issue-item">
                                        <v-icon size="14">mdi-alert-circle</v-icon>
                                        <span>{{ $t('operations.categories.compatibility.issue2') }}</span>
                                    </div>
                                    <div class="issue-item">
                                        <v-icon size="14">mdi-alert-circle</v-icon>
                                        <span>{{ $t('operations.categories.compatibility.issue3') }}</span>
                                    </div>
                                    <div class="issue-item">
                                        <v-icon size="14">mdi-alert-circle</v-icon>
                                        <span>{{ $t('operations.categories.compatibility.issue4') }}</span>
                                    </div>
                                </div>
                                <div class="category-stats">
                                    <span class="stat-badge">{{ $t('operations.categories.compatibility.avgResolution') }}</span>
                                </div>
                            </div>
                        </v-col>

                        <!-- Integration Failures -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="category-card integration">
                                <div class="category-header">
                                    <div class="category-icon">
                                        <v-icon size="36" color="white">mdi-puzzle</v-icon>
                                    </div>
                                    <div class="priority-badge high">{{ $t('operations.categories.integration.priority') }}</div>
                                </div>
                                <h4>{{ $t('operations.categories.integration.title') }}</h4>
                                <p>{{ $t('operations.categories.integration.description') }}</p>
                                <div class="issue-list">
                                    <div class="issue-item">
                                        <v-icon size="14">mdi-alert-circle</v-icon>
                                        <span>{{ $t('operations.categories.integration.issue1') }}</span>
                                    </div>
                                    <div class="issue-item">
                                        <v-icon size="14">mdi-alert-circle</v-icon>
                                        <span>{{ $t('operations.categories.integration.issue2') }}</span>
                                    </div>
                                    <div class="issue-item">
                                        <v-icon size="14">mdi-alert-circle</v-icon>
                                        <span>{{ $t('operations.categories.integration.issue3') }}</span>
                                    </div>
                                    <div class="issue-item">
                                        <v-icon size="14">mdi-alert-circle</v-icon>
                                        <span>{{ $t('operations.categories.integration.issue4') }}</span>
                                    </div>
                                </div>
                                <div class="category-stats">
                                    <span class="stat-badge">{{ $t('operations.categories.integration.avgResolution') }}</span>
                                </div>
                            </div>
                        </v-col>
                    </v-row>
                </div>

                <!-- Support Tools -->
                <div class="tools-section">
                    <div class="section-header">
                        <h3>{{ $t('operations.tools.title') }}</h3>
                        <p>{{ $t('operations.tools.subtitle') }}</p>
                    </div>

                    <v-row class="tools-grid">
                        <!-- SLA Tracking -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="tool-card">
                                <div class="tool-icon">
                                    <v-icon size="40" color="white">mdi-clock-outline</v-icon>
                                </div>
                                <h4>{{ $t('operations.tools.slaTracking.title') }}</h4>
                                <p>{{ $t('operations.tools.slaTracking.description') }}</p>
                                <div class="tool-features">
                                    <div class="feature">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('operations.tools.slaTracking.feature1') }}</span>
                                    </div>
                                    <div class="feature">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('operations.tools.slaTracking.feature2') }}</span>
                                    </div>
                                    <div class="feature">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('operations.tools.slaTracking.feature3') }}</span>
                                    </div>
                                    <div class="feature">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('operations.tools.slaTracking.feature4') }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <!-- Status Transparency -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="tool-card">
                                <div class="tool-icon">
                                    <v-icon size="40" color="white">mdi-eye-outline</v-icon>
                                </div>
                                <h4>{{ $t('operations.tools.statusTransparency.title') }}</h4>
                                <p>{{ $t('operations.tools.statusTransparency.description') }}</p>
                                <div class="tool-features">
                                    <div class="feature">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('operations.tools.statusTransparency.feature1') }}</span>
                                    </div>
                                    <div class="feature">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('operations.tools.statusTransparency.feature2') }}</span>
                                    </div>
                                    <div class="feature">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('operations.tools.statusTransparency.feature3') }}</span>
                                    </div>
                                    <div class="feature">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('operations.tools.statusTransparency.feature4') }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <!-- Admin Control -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="tool-card">
                                <div class="tool-icon">
                                    <v-icon size="40" color="white">mdi-shield-account</v-icon>
                                </div>
                                <h4>{{ $t('operations.tools.adminControl.title') }}</h4>
                                <p>{{ $t('operations.tools.adminControl.description') }}</p>
                                <div class="tool-features">
                                    <div class="feature">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('operations.tools.adminControl.feature1') }}</span>
                                    </div>
                                    <div class="feature">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('operations.tools.adminControl.feature2') }}</span>
                                    </div>
                                    <div class="feature">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('operations.tools.adminControl.feature3') }}</span>
                                    </div>
                                    <div class="feature">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('operations.tools.adminControl.feature4') }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <!-- Knowledge Base -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="tool-card">
                                <div class="tool-icon">
                                    <v-icon size="40" color="white">mdi-book-open-variant</v-icon>
                                </div>
                                <h4>{{ $t('operations.tools.knowledgeBase.title') }}</h4>
                                <p>{{ $t('operations.tools.knowledgeBase.description') }}</p>
                                <div class="tool-features">
                                    <div class="feature">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('operations.tools.knowledgeBase.feature1') }}</span>
                                    </div>
                                    <div class="feature">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('operations.tools.knowledgeBase.feature2') }}</span>
                                    </div>
                                    <div class="feature">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('operations.tools.knowledgeBase.feature3') }}</span>
                                    </div>
                                    <div class="feature">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('operations.tools.knowledgeBase.feature4') }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <!-- Analytics Dashboard -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="tool-card">
                                <div class="tool-icon">
                                    <v-icon size="40" color="white">mdi-chart-bar</v-icon>
                                </div>
                                <h4>{{ $t('operations.tools.analytics.title') }}</h4>
                                <p>{{ $t('operations.tools.analytics.description') }}</p>
                                <div class="tool-features">
                                    <div class="feature">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('operations.tools.analytics.feature1') }}</span>
                                    </div>
                                    <div class="feature">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('operations.tools.analytics.feature2') }}</span>
                                    </div>
                                    <div class="feature">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('operations.tools.analytics.feature3') }}</span>
                                    </div>
                                    <div class="feature">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('operations.tools.analytics.feature4') }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <!-- Automation Rules -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="tool-card">
                                <div class="tool-icon">
                                    <v-icon size="40" color="white">mdi-robot-outline</v-icon>
                                </div>
                                <h4>{{ $t('operations.tools.automation.title') }}</h4>
                                <p>{{ $t('operations.tools.automation.description') }}</p>
                                <div class="tool-features">
                                    <div class="feature">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('operations.tools.automation.feature1') }}</span>
                                    </div>
                                    <div class="feature">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('operations.tools.automation.feature2') }}</span>
                                    </div>
                                    <div class="feature">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('operations.tools.automation.feature3') }}</span>
                                    </div>
                                    <div class="feature">
                                        <v-icon size="16">mdi-check</v-icon>
                                        <span>{{ $t('operations.tools.automation.feature4') }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>
                    </v-row>
                </div>
            </div>
        </v-card>
    </v-dialog>
</template>

<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const visible = ref(false);

function open() { 
    visible.value = true; 
}

function closeDialog() { 
    visible.value = false; 
}

defineExpose({ open, close: closeDialog });
</script>
<style scoped>
/* Card Base */
.operations-card {
    background: linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%);
    min-height: 100vh;
    position: relative;
}

/* Header */
.card-header {
    background: linear-gradient(135deg, #0A1929 0%, #1A2F42 100%);
    padding: 32px 40px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    position: sticky;
    top: 0;
    z-index: 100;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.left-section {
    flex: 1;
}

.header-badge {
    display: inline-block;
    padding: 6px 16px;
    background: rgba(30, 90, 142, 0.2);
    border: 1px solid rgba(30, 90, 142, 0.3);
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1px;
    color: #60a5fa;
    margin-bottom: 16px;
}

.card-header h2 {
    font-size: 32px;
    font-weight: 800;
    color: #ffffff;
    margin: 0 0 8px 0;
    letter-spacing: -0.5px;
}

.header-description {
    font-size: 15px;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.5;
    margin: 0;
}

.right-section {
    margin-left: 24px;
}

.dialog-close-btn {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
}

.dialog-close-btn:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
}

.dialog-close-btn .v-icon {
    color: white;
}

/* Main Container */
.operations-container {
    padding: 40px;
    max-width: 1600px;
    margin: 0 auto;
}

/* Section Styling */
.workflow-section,
.categories-section,
.tools-section {
    margin-bottom: 56px;
}

.section-header {
    text-align: center;
    margin-bottom: 40px;
}

.section-header h3 {
    font-size: 32px;
    font-weight: 800;
    color: #1f2937;
    margin: 0 0 12px 0;
    letter-spacing: -0.5px;
}

.section-header p {
    font-size: 16px;
    color: #6b7280;
    margin: 0;
}

/* Workflow Diagram */
.workflow-diagram {
    background: white;
    border-radius: 24px;
    padding: 48px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
    border: 1px solid rgba(0, 0, 0, 0.04);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    overflow-x: auto;
}

.workflow-step {
    position: relative;
    flex-shrink: 0;
}

.step-number {
    position: absolute;
    top: -12px;
    left: 50%;
    transform: translateX(-50%);
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, #1E5A8E 0%, #2B7BC4 100%);
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 700;
    box-shadow: 0 4px 12px rgba(30, 90, 142, 0.3);
}

.step-content {
    background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
    border-radius: 16px;
    padding: 28px 24px;
    text-align: center;
    width: 180px;
    border: 2px solid #e5e7eb;
    transition: all 0.3s ease;
}

.workflow-step:hover .step-content {
    border-color: #1E5A8E;
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(30, 90, 142, 0.15);
}

.step-icon {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    background: linear-gradient(135deg, #1E5A8E 0%, #2B7BC4 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
}

.step-content h4 {
    font-size: 16px;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 8px 0;
}

.step-content p {
    font-size: 12px;
    color: #6b7280;
    margin: 0;
    line-height: 1.5;
}

.workflow-arrow {
    color: #1E5A8E;
    flex-shrink: 0;
}

/* Category Cards */
.category-card {
    background: white;
    border-radius: 20px;
    padding: 28px;
    height: 100%;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    border: 2px solid transparent;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.category-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
}

.category-card.login:hover { border-color: #1E5A8E; }
.category-card.performance:hover { border-color: #FF6B35; }
.category-card.notifications:hover { border-color: #fbbf24; }
.category-card.sync:hover { border-color: #06b6d4; }
.category-card.compatibility:hover { border-color: #10b981; }
.category-card.integration:hover { border-color: #8b5cf6; }

.category-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.category-icon {
    width: 72px;
    height: 72px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.4s ease;
}

.category-card:hover .category-icon {
    transform: scale(1.1) rotate(-5deg);
}

.category-card.login .category-icon {
    background: linear-gradient(135deg, #1E5A8E 0%, #2B7BC4 100%);
}

.category-card.performance .category-icon {
    background: linear-gradient(135deg, #FF6B35 0%, #FFA07A 100%);
}

.category-card.notifications .category-icon {
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
}

.category-card.sync .category-icon {
    background: linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%);
}

.category-card.compatibility .category-icon {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.category-card.integration .category-icon {
    background: linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%);
}

.priority-badge {
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.priority-badge.high {
    background: #fee2e2;
    color: #dc2626;
}

.priority-badge.medium {
    background: #fef3c7;
    color: #d97706;
}

.priority-badge.low {
    background: #dbeafe;
    color: #1e40af;
}

.category-card h4 {
    font-size: 20px;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 12px 0;
}

.category-card > p {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.7;
    margin: 0 0 20px 0;
}

.issue-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 20px;
}

.issue-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #4b5563;
    font-weight: 500;
}

.issue-item .v-icon {
    color: #ef4444;
}

.category-stats {
    padding-top: 20px;
    border-top: 2px solid #f3f4f6;
}

.stat-badge {
    display: inline-block;
    padding: 6px 12px;
    background: #ecfdf5;
    color: #059669;
    font-size: 12px;
    font-weight: 600;
    border-radius: 20px;
}

/* Tool Cards */
.tool-card {
    background: white;
    border-radius: 20px;
    padding: 28px;
    height: 100%;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    border: 2px solid transparent;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    text-align: center;
}

.tool-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
    border-color: #1E5A8E;
}

.tool-icon {
    width: 80px;
    height: 80px;
    border-radius: 16px;
    background: linear-gradient(135deg, #1E5A8E 0%, #2B7BC4 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    transition: transform 0.4s ease;
}

.tool-card:hover .tool-icon {
    transform: scale(1.1) rotate(5deg);
}

.tool-card h4 {
    font-size: 20px;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 12px 0;
}

.tool-card > p {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.7;
    margin: 0 0 20px 0;
}

.tool-features {
    display: flex;
    flex-direction: column;
    gap: 10px;
    text-align: left;
}

.feature {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #4b5563;
    font-weight: 500;
}

.feature .v-icon {
    color: #10b981;
}

/* Responsive */
@media (max-width: 1024px) {
    .operations-container {
        padding: 32px 24px;
    }

    .workflow-diagram {
        flex-direction: column;
        padding: 32px 24px;
    }

    .workflow-arrow {
        transform: rotate(90deg);
    }
}

@media (max-width: 768px) {
    .card-header {
        padding: 24px;
        flex-direction: column;
        gap: 20px;
    }

    .card-header h2 {
        font-size: 24px;
    }

    .header-description {
        font-size: 14px;
    }

    .right-section {
        margin-left: 0;
        align-self: flex-end;
    }

    .operations-container {
        padding: 24px 16px;
    }

    .section-header h3 {
        font-size: 24px;
    }

    .workflow-diagram {
        padding: 24px 16px;
    }

    .step-content {
        width: 160px;
    }
}

@media (max-width: 480px) {
    .card-header h2 {
        font-size: 20px;
    }

    .operations-container {
        padding: 20px 12px;
    }
}

/* Scrollbar */
.operations-container::-webkit-scrollbar {
    width: 8px;
}

.operations-container::-webkit-scrollbar-track {
    background: #f3f4f6;
}

.operations-container::-webkit-scrollbar-thumb {
    background: #9ca3af;
    border-radius: 4px;
}

.operations-container::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
}

.workflow-diagram::-webkit-scrollbar {
    height: 6px;
}

.workflow-diagram::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
}
</style>